/**
 * Single source of truth for the "remembered email" used across free-tier
 * flows (save, free-up-a-slot, upgrade checkout). Pulls from `localStorage`
 * first (cheap, sync), then falls back to the active Supabase auth session.
 *
 * Centralised so we don't have ad-hoc `localStorage.getItem("cp_email")`
 * reads scattered across components — and so sign-out has one place to clear.
 */
import { supabase } from "@/integrations/supabase/client";

const KEY = "cp_email";

export function getRememberedEmail(): string {
  try {
    const cached = localStorage.getItem(KEY);
    if (cached) return cached;
  } catch {
    // ignore — private mode etc.
  }
  return "";
}

/**
 * Async variant that also consults the live Supabase session, used when the
 * caller wants to recover an email even if `cp_email` was cleared.
 */
export async function getRememberedEmailAsync(): Promise<string> {
  const cached = getRememberedEmail();
  if (cached) return cached;
  try {
    const { data } = await supabase.auth.getSession();
    const sessionEmail = data.session?.user?.email?.toLowerCase().trim() ?? "";
    if (sessionEmail) {
      // Opportunistically persist so subsequent sync reads work.
      setRememberedEmail(sessionEmail);
      return sessionEmail;
    }
  } catch {
    // ignore
  }
  return "";
}

/**
 * Session-first email resolver. The active Supabase session ALWAYS wins
 * over the `cp_email` cache — this prevents cross-account leakage on shared
 * devices where a previous user's email may still be cached.
 *
 * Use this in any flow where the answer to "which account am I acting on
 * behalf of?" matters: saving recipes, freeing slots, checkout, etc.
 *
 * Returns:
 *   { email, fromSession }
 *   - `fromSession: true` means we found a live signed-in user.
 *   - `fromSession: false` means we fell back to the local cache (or empty).
 */
export async function getActiveEmail(): Promise<{
  email: string;
  fromSession: boolean;
}> {
  try {
    const { data } = await supabase.auth.getSession();
    const sessionEmail = data.session?.user?.email?.toLowerCase().trim() ?? "";
    if (sessionEmail) {
      // Keep the cache in sync so any sync `getRememberedEmail()` reads
      // elsewhere see the right value too.
      setRememberedEmail(sessionEmail);
      return { email: sessionEmail, fromSession: true };
    }
  } catch {
    // ignore — fall back to cache
  }
  return { email: getRememberedEmail(), fromSession: false };
}

export function setRememberedEmail(email: string): void {
  const normalised = (email || "").toLowerCase().trim();
  if (!normalised) return;
  try {
    localStorage.setItem(KEY, normalised);
  } catch {
    // ignore
  }
}

export function clearRememberedEmail(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
